import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { flushSync } from 'react-dom'
import { updateProject } from '../lib/api'
import { supabase } from '../lib/supabase'
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts'

// recharts' ResponsiveContainer calls getBoundingClientRect() on mount, which returns
// width=0 when the parent has overflow:hidden + height:0 (the hidden-for-print state).
// A zero width causes ResponsiveContainerContextProvider to return null (no SVG in DOM).
// This wrapper remembers the last VALID (>0) width so the SVG always exists in the DOM.
//
// Print sizing: Chrome fires beforeprint BEFORE applying print CSS, so measuring
// getBoundingClientRect() there returns the screen width — not the print width.
// Instead we use the known print column width directly: @page is 17in x 11in landscape
// with 0.25in margins -> 16.5in content -> two flex-1 columns with px-6 padding -> 744px.
// flushSync forces the re-render to complete before the browser captures the print bitmap.
// afterprint restores the live screen measurement.
const PRINT_CHART_COL_WIDTH = 744 // (16.5in x 96dpi) / 2 cols - 48px px-6 padding

function SafeResponsiveContainer({ height, children }) {
  const divRef = useRef(null)
  const [width, setWidth] = useState(700) // safe default; updated to real width on mount

  useEffect(() => {
    if (!divRef.current) return
    const update = w => { if (w > 0) setWidth(Math.floor(w)) }
    const ro = new ResizeObserver(entries => update(entries[0]?.contentRect.width ?? 0))
    ro.observe(divRef.current)
    update(divRef.current.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const beforePrint = () => {
      flushSync(() => setWidth(PRINT_CHART_COL_WIDTH))
    }
    const afterPrint = () => {
      if (!divRef.current) return
      const w = divRef.current.getBoundingClientRect().width
      if (w > 0) flushSync(() => setWidth(Math.floor(w)))
    }
    window.addEventListener('beforeprint', beforePrint)
    window.addEventListener('afterprint', afterPrint)
    return () => {
      window.removeEventListener('beforeprint', beforePrint)
      window.removeEventListener('afterprint', afterPrint)
    }
  }, [])

  return (
    <div ref={divRef} style={{ width: '100%', height }}>
      {React.cloneElement(children, { width, height })}
    </div>
  )
}

const DEFAULT_COLORS = [
  '#1e3a8a', '#D22630', '#065f46', '#92400e', '#4c1d95',
  '#0e7490', '#9f1239', '#374151', '#1e40af', '#c2410c',
]

// Matches DEPT_COLORS hex values in App.jsx
const DEPT_DEFAULT_COLORS = {
  'Avionics':          '#3b82f6', // AVI
  'Interior':          '#f97316', // INT
  'Jet Shop / Piston': '#22c55e', // MX
  'Paint':             '#ec4899', // PA
  'Shop Helpers':      '#9ca3af', // PARTS
  'Admin/Management':  '#1e3a8a',
  'Inspection':        '#eab308', // QA
  'Modifications':     '#0ea5e9', // MOD
}

const R = Math.PI / 180


function getColor(colors, i, row) {
  if (row?.tsheets) return DEPT_DEFAULT_COLORS[row.dept] ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]
  return colors[i] ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]
}

// ── PIE CALLOUT LABEL (outside slice with elbow line) ────────────────────────

function PieCalloutLabel({ cx, cy, midAngle, outerRadius, percent, name }) {
  if (percent < 0.04) return null

  const cos = Math.cos(-midAngle * R)
  const sin = Math.sin(-midAngle * R)

  const x1 = cx + (outerRadius + 8) * cos
  const y1 = cy + (outerRadius + 8) * sin
  const x2 = cx + (outerRadius + 30) * cos
  const y2 = cy + (outerRadius + 30) * sin
  const isRight = cos >= 0
  const x3 = x2 + (isRight ? 20 : -20)
  const y3 = y2
  const tx = x3 + (isRight ? 4 : -4)
  const anchor = isRight ? 'start' : 'end'
  const pct = `${(percent * 100).toFixed(1)}%`

  return (
    <g>
      <polyline
        points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
        fill="none" stroke="#6b7280" strokeWidth={1.5}
      />
      <text x={tx} y={y3 - 8} textAnchor={anchor} fill="#111827" fontSize={14} fontWeight="700">
        {name}
      </text>
      <text x={tx} y={y3 + 9} textAnchor={anchor} fill="#374151" fontSize={13} fontWeight="700">
        {pct}
      </text>
    </g>
  )
}

// ── BAR VALUE LABEL (above each bar) ─────────────────────────────────────────

function BarValueLabel({ x, y, width, value }) {
  if (!value) return null
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      textAnchor="middle"
      fill="#111827"
      fontSize={10}
      fontWeight="700"
    >
      {value % 1 === 0 ? value : value.toFixed(1)}
    </text>
  )
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function ProjectChartsPanel({ projectId, project, snapshotState }) {
  const [rows, setRows] = useState([{ dept: '', actual: '', quoted: '' }])
  const [colors, setColors] = useState([...DEFAULT_COLORS])
  const [colorPickerIdx, setColorPickerIdx] = useState(null)
  const [tsheetsDeptHours,     setTsheetsDeptHours]     = useState(null) // { [deptName]: hours }
  const [tsheetsDeptTechHours, setTsheetsDeptTechHours] = useState(null) // { [deptName]: { [techName]: hours } }
  const [selectedDept,         setSelectedDept]         = useState(null) // dept name for tech breakdown modal

  const serverLoaded = useRef(false)
  const rowsRef = useRef(rows)
  const colorsRef = useRef(colors)
  rowsRef.current = rows
  colorsRef.current = colors
  const saveTimeout = useRef(null)
  const [refreshing, setRefreshing] = useState(false)

  // Fetch TSheets dept_hours for this project
  const fetchTsheetsHours = useCallback(async () => {
    if (!project) return
    const registration = project.aircraft?.registration || ''
    const secondary    = project.secondary_registration || ''
    const serialNumber = project.aircraft?.serial_number || ''
    const startDate    = project.arrival_date || ''
    if (!registration || !startDate) return
    setRefreshing(true)
    const queries = [
      supabase.from('tsheets_hours_cache').select('dept_hours, dept_tech_hours').eq('registration', registration).eq('start_date', startDate).maybeSingle(),
      secondary
        ? supabase.from('tsheets_hours_cache').select('dept_hours, dept_tech_hours').eq('registration', secondary).eq('start_date', startDate).maybeSingle()
        : Promise.resolve({ data: null }),
    ]
    const [primary, sec] = await Promise.all(queries)
    const hasHours = (src) => Object.keys(src?.dept_hours || {}).length > 0
    // fallback: if neither registration nor secondary has actual hours, try a contains-match on serial number
    let serialRows = []
    if (!hasHours(primary.data) && !hasHours(sec.data) && serialNumber) {
      const { data: sd } = await supabase
        .from('tsheets_hours_cache')
        .select('dept_hours, dept_tech_hours')
        .ilike('registration', `%${serialNumber}%`)
        .eq('start_date', startDate)
      serialRows = sd || []
    }
    const combinedDept = {}
    const combinedDeptTech = {}
    for (const src of [primary.data, sec.data, ...serialRows]) {
      if (!src) continue
      for (const [k, v] of Object.entries(src.dept_hours || {}))
        combinedDept[k] = (combinedDept[k] || 0) + v
      for (const [dept, techMap] of Object.entries(src.dept_tech_hours || {})) {
        if (!combinedDeptTech[dept]) combinedDeptTech[dept] = {}
        for (const [tech, hrs] of Object.entries(techMap))
          combinedDeptTech[dept][tech] = (combinedDeptTech[dept][tech] || 0) + hrs
      }
    }
    if (Object.keys(combinedDept).length > 0) setTsheetsDeptHours(combinedDept)
    if (Object.keys(combinedDeptTech).length > 0) setTsheetsDeptTechHours(combinedDeptTech)
    setRefreshing(false)
  }, [project])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await supabase.functions.invoke('tsheets-sync')
    } catch (e) {
      console.warn('[tsheets refresh] sync invoke failed:', e)
    }
    await fetchTsheetsHours()
  }, [fetchTsheetsHours])

  useEffect(() => { fetchTsheetsHours() }, [project?.id])  // eslint-disable-line react-hooks/exhaustive-deps

  // Keep TSheets rows in sync when dept_hours loads or changes
  const tsheetsDepts = useMemo(
    () => tsheetsDeptHours ? Object.keys(tsheetsDeptHours).sort() : [],
    [tsheetsDeptHours]
  )
  useEffect(() => {
    if (tsheetsDepts.length === 0) return
    setRows(prev => {
      let next = [...prev]
      let changed = false
      // Add missing TSheets rows, preserving any quoted value from a matching manual row
      for (const dept of tsheetsDepts) {
        if (!next.some(r => r.tsheets && r.dept === dept)) {
          const existingManual = next.find(r => !r.tsheets && r.dept === dept)
          next = [...next, { dept, actual: '', quoted: existingManual?.quoted ?? '', tsheets: true }]
          changed = true
        }
      }
      // Remove TSheets rows for depts no longer in cache
      const afterStale = next.filter(r => !r.tsheets || tsheetsDepts.includes(r.dept))
      if (afterStale.length !== next.length) { next = afterStale; changed = true }
      // Remove manual rows whose dept is now covered by a TSheets row
      const afterDups = next.filter(r => r.tsheets || !tsheetsDepts.includes(r.dept))
      if (afterDups.length !== next.length) { next = afterDups; changed = true }
      return changed ? next : prev
    })
  }, [tsheetsDepts.join(',')])  // eslint-disable-line react-hooks/exhaustive-deps

  // Sync from Supabase once project data arrives; migrate from localStorage if Supabase is empty
  useEffect(() => {
    if (serverLoaded.current || !project) return
    serverLoaded.current = true

    if (project.chart_rows != null) {
      setRows(project.chart_rows)
      if (project.chart_colors != null) setColors(project.chart_colors)
    } else {
      // One-time migration: push any existing localStorage data up to Supabase
      const lsRows = (() => { try { return JSON.parse(localStorage.getItem(`chart_rows_${projectId}`)) } catch { return null } })()
      const lsColors = (() => { try { return JSON.parse(localStorage.getItem(`chart_colors_${projectId}`)) } catch { return null } })()
      if (lsRows) {
        const migratedColors = lsColors ?? [...DEFAULT_COLORS]
        setRows(lsRows)
        setColors(migratedColors)
        updateProject(projectId, {
          chart_rows: lsRows,
          chart_colors: migratedColors,
        }).then(({ error }) => {
          if (error) console.error('[chart migrate]', error)
          else {
            localStorage.removeItem(`chart_rows_${projectId}`)
            localStorage.removeItem(`chart_colors_${projectId}`)
          }
        })
      }
    }
  }, [project, projectId])

  const scheduleSave = useCallback(() => {
    if (!serverLoaded.current) return
    clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(async () => {
      const { error } = await updateProject(projectId, {
        chart_rows: rowsRef.current,
        chart_colors: colorsRef.current,
      })
      if (error) console.error('[chart save]', error)
    }, 800)
  }, [projectId])

  useEffect(() => {
    scheduleSave()
    return () => clearTimeout(saveTimeout.current)
  }, [rows, colors, scheduleSave])

  const addRow = () => setRows(r => [...r, { dept: '', actual: '', quoted: '' }])
  const removeRow = i => setRows(r => r.filter((_, j) => j !== i))
  const updateRow = (i, field, val) =>
    setRows(r => r.map((row, j) => j === i ? { ...row, [field]: val } : row))

  const setColor = (i, color) => setColors(c => {
    const next = [...c]
    while (next.length <= i) next.push(DEFAULT_COLORS[next.length % DEFAULT_COLORS.length])
    next[i] = color
    return next
  })

  const tsheetsRows = rows.filter(r => r.tsheets)
  const manualRows  = rows.filter(r => !r.tsheets)

  // When printing a snapshot, use its chart_rows/colors so the charts reflect that moment in time.
  // TSheets actuals are still live (same aircraft + arrival_date = same cache key), which is fine.
  const displayRows   = snapshotState?.project?.chart_rows   ?? rows
  const displayColors = snapshotState?.project?.chart_colors ?? colors

  const snapshotTsheetsHours = snapshotState?.tsheetsDeptHours

  const validRows = displayRows
    .map((r, i) => ({
      idx: i,
      dept: r.dept.trim(),
      actual: r.tsheets
        ? (snapshotTsheetsHours?.[r.dept] ?? tsheetsDeptHours?.[r.dept] ?? 0)
        : (parseFloat(r.actual) || 0),
      quoted: parseFloat(r.quoted) || 0,
    }))
    .filter(r => r.dept)

  const totalActual = validRows.reduce((s, r) => s + r.actual, 0)
  const totalQuoted = validRows.reduce((s, r) => s + r.quoted, 0)

  const pieData = validRows
    .filter(r => r.actual > 0)
    .map(r => ({ name: r.dept, value: r.actual, rowIdx: r.idx }))

  const barData = [
    ...validRows.map(r => ({
      name: r.dept,
      Actual: r.actual,
      Quoted: r.quoted,
      rowIdx: r.idx,
    })),
    { name: 'Total', Actual: totalActual, Quoted: totalQuoted, isTotal: true },
  ]

  // Custom pie legend with click-to-edit color swatches
  const renderPieLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1" style={{ width: '100%' }}>
      {(payload ?? []).map((entry, i) => {
        const rowIdx = pieData.find(p => p.name === entry.value)?.rowIdx
        return (
          <div key={i} className="flex items-center gap-1.5">
            <button
              className="w-3.5 h-3.5 rounded-sm border border-white shadow hover:scale-125 transition-transform"
              style={{ backgroundColor: entry.color }}
              onClick={() => {
                const row = rows[rowIdx]
                if (row?.tsheets) setSelectedDept(row.dept)
                else setColorPickerIdx(rowIdx)
              }}
              title={rows[rowIdx]?.tsheets ? 'Click to see technician breakdown' : 'Click to change color'}
            />
            <span className="text-xs font-semibold text-gray-700">{entry.value}</span>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="bg-white">
      {/* Brand header */}
      <div className="px-5 py-2.5" style={{ backgroundColor: '#101820' }}>
        <div className="flex items-center justify-between">
          <div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', letterSpacing: '0.08em', color: 'white', lineHeight: 1.1 }}>
              Hours Analysis
            </div>
            {project && (
              <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {[project.aircraft?.registration, project.customer_name].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Sync from TSheets then refresh"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold transition-opacity"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.75)', opacity: refreshing ? 0.5 : 1 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582M20 20v-5h-.581M5.635 19A9 9 0 104.583 9.5" />
              </svg>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <img src="/logo-alt.png" alt="Clemens Aviation" className="h-7 w-auto" />
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="flex border-b border-gray-200">
        {/* Pie chart */}
        <div className="flex-1 px-6 pt-4 pb-2 border-r border-gray-100">
          <div className="text-sm font-bold text-center text-gray-800 uppercase tracking-widest mb-2">
            Actual Hours Breakdown
          </div>
          <SafeResponsiveContainer height={520}>
            <PieChart margin={{ top: 30, right: 70, bottom: 30, left: 70 }}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={155}
                labelLine={false}
                label={PieCalloutLabel}
                isAnimationActive={false}
                onClick={(data) => {
                  const row = rows[data.rowIdx]
                  if (row?.tsheets) setSelectedDept(row.dept)
                  else setColorPickerIdx(data.rowIdx)
                }}
                style={{ cursor: 'pointer' }}
              >
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={getColor(displayColors, entry.rowIdx, displayRows[entry.rowIdx])} stroke={pieData.length > 1 ? 'white' : 'none'} strokeWidth={pieData.length > 1 ? 2 : 0} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => `${(v / (totalActual || 1) * 100).toFixed(1)}%`}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Legend content={renderPieLegend} />
            </PieChart>
          </SafeResponsiveContainer>
        </div>

        {/* Bar chart */}
        <div className="flex-1 px-6 pt-4 pb-2">
          <div className="text-sm font-bold text-center text-gray-800 uppercase tracking-widest mb-2">
            Quoted vs. Actual Breakdown
          </div>
          <SafeResponsiveContainer height={520}>
            <BarChart data={barData} margin={{ top: 24, right: 60, left: 0, bottom: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: '#111827', fontWeight: 600 }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }}
                axisLine={false} tickLine={false}
                width={44}
              />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} formatter={(v) => v % 1 === 0 ? v : parseFloat(v.toFixed(1))} />
              <Legend
                wrapperStyle={{ fontSize: 13, fontWeight: 700, color: '#111827' }}
              />
              <Bar dataKey="Actual" name="Actual" fill="#1e3a8a" radius={[4, 4, 0, 0]} maxBarSize={52}
                isAnimationActive={false} label={<BarValueLabel />} />
              <Bar dataKey="Quoted" name="Quoted" fill="#c2410c" radius={[4, 4, 0, 0]} maxBarSize={52}
                isAnimationActive={false} label={<BarValueLabel />} />
            </BarChart>
          </SafeResponsiveContainer>
        </div>
      </div>

      {/* Color picker overlay */}
      {colorPickerIdx !== null && (
        <div
          className="no-print fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          onClick={() => setColorPickerIdx(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 flex flex-col items-center gap-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-sm font-semibold text-gray-700">
              Color — {rows[colorPickerIdx]?.dept || `Row ${colorPickerIdx + 1}`}
            </div>
            <input
              type="color"
              value={getColor(colors, colorPickerIdx, rows[colorPickerIdx])}
              onChange={e => setColor(colorPickerIdx, e.target.value)}
              className="w-20 h-20 rounded-xl cursor-pointer border-0 p-0"
            />
            <button
              onClick={() => setColorPickerIdx(null)}
              className="text-xs font-semibold px-5 py-2 rounded-lg text-white"
              style={{ backgroundColor: '#D22630' }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Tech breakdown modal — shown when a TSheets dept is clicked */}
      {selectedDept && (() => {
        const techMap = tsheetsDeptTechHours?.[selectedDept] || {}
        const techs = Object.entries(techMap).sort((a, b) => b[1] - a[1])
        const deptTotal = techs.reduce((s, [, h]) => s + h, 0)
        return (
          <div
            className="no-print fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
            onClick={() => setSelectedDept(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-5 py-3.5" style={{ backgroundColor: '#101820' }}>
                <div className="text-xs font-bold tracking-widest uppercase text-white/50 mb-0.5">Technician Hours</div>
                <div className="text-base font-bold text-white">{selectedDept}</div>
                <div className="text-xs text-white/40 mt-0.5">{deptTotal.toFixed(1)} total hours</div>
              </div>
              <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                {techs.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-gray-400 italic">No data yet — sync pending.</div>
                ) : techs.map(([name, hrs]) => {
                  const pct = deptTotal > 0 ? (hrs / deptTotal) * 100 : 0
                  return (
                    <div key={name} className="flex items-center gap-3 px-5 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{name}</div>
                        <div className="mt-1 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: DEPT_DEFAULT_COLORS[selectedDept] ?? '#1e3a8a' }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-gray-800">{hrs % 1 === 0 ? hrs : hrs.toFixed(1)}h</div>
                        <div className="text-[11px] text-gray-400">{pct.toFixed(0)}%</div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setSelectedDept(null)}
                  className="text-xs font-semibold px-4 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: '#D22630' }}
                >Close</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Table */}
      <div className="px-4 pt-1 pb-3">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr style={{ backgroundColor: '#101820' }}>
              <th className="py-2 px-3 text-left text-xs font-bold tracking-widest text-white uppercase">Department</th>
              <th className="py-2 px-3 text-right text-xs font-bold tracking-widest text-white uppercase">Actual</th>
              <th className="py-2 px-3 text-right text-xs font-bold tracking-widest text-white uppercase">Quoted</th>
              <th className="no-print py-2 px-2 w-6" />
            </tr>
          </thead>
          <tbody>
            {/* TSheets auto-populated rows — actual locked, quoted editable */}
            {tsheetsRows.map((row) => {
              const globalIdx = rows.indexOf(row)
              const actual = tsheetsDeptHours?.[row.dept] || 0
              return (
                <tr key={`ts-${row.dept}`} className="bg-gray-50/80">
                  <td className="py-1 px-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedDept(row.dept)}
                        className="no-print text-sm font-medium text-gray-800 hover:text-brand-red transition-colors text-left"
                        title="Click to see technician breakdown"
                      >{row.dept}</button>
                      <span className="hidden print:inline text-sm font-medium text-gray-800">{row.dept}</span>
                      <span className="no-print text-[10px] font-semibold text-brand-red bg-red-50 px-1.5 py-0.5 rounded">TSheets</span>
                    </div>
                  </td>
                  <td className="py-1 px-3 border-b border-gray-100 text-right">
                    <span className="text-sm font-medium text-gray-700">{actual.toFixed(1)}</span>
                  </td>
                  <td className="py-1 px-3 border-b border-gray-100 text-right">
                    <input
                      type="number" step="0.01" min="0"
                      value={row.quoted}
                      onChange={e => updateRow(globalIdx, 'quoted', e.target.value)}
                      className="w-28 text-sm font-medium text-gray-800 bg-transparent focus:outline-none text-right"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="no-print py-1.5 px-1 border-b border-gray-100" />
                </tr>
              )
            })}
            {/* Manual rows */}
            {manualRows.map((row) => {
              const i = rows.indexOf(row)
              const isEmpty = !row.dept && !row.actual && !row.quoted
              return (
                <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}${isEmpty ? ' no-print' : ''}`}>
                  <td className="py-1 px-3 border-b border-gray-100">
                    <input
                      value={row.dept}
                      onChange={e => updateRow(i, 'dept', e.target.value)}
                      className="w-full text-sm font-medium text-gray-800 bg-transparent focus:outline-none"
                      placeholder="Department name"
                    />
                  </td>
                  <td className="py-1 px-3 border-b border-gray-100 text-right">
                    <input
                      type="number" step="0.01" min="0"
                      value={row.actual}
                      onChange={e => updateRow(i, 'actual', e.target.value)}
                      className="w-28 text-sm font-medium text-gray-800 bg-transparent focus:outline-none text-right"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="py-1 px-3 border-b border-gray-100 text-right">
                    <input
                      type="number" step="0.01" min="0"
                      value={row.quoted}
                      onChange={e => updateRow(i, 'quoted', e.target.value)}
                      className="w-28 text-sm font-medium text-gray-800 bg-transparent focus:outline-none text-right"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="no-print py-1.5 px-1 border-b border-gray-100 text-center">
                    <button onClick={() => removeRow(i)}
                      className="text-gray-300 hover:text-red-400 transition-colors p-0.5 rounded">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#101820' }}>
              <td className="py-2 px-3 text-sm font-extrabold text-white uppercase tracking-widest">Total</td>
              <td className="py-2 px-3 text-sm font-extrabold text-white text-right">{totalActual.toFixed(2)}</td>
              <td className="py-2 px-3 text-sm font-extrabold text-white text-right">{totalQuoted.toFixed(2)}</td>
              <td className="no-print" />
            </tr>
          </tfoot>
        </table>

        <button onClick={addRow}
          className="no-print mt-2 flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Row
        </button>
      </div>
    </div>
  )
}
